/* External */
import { useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import isEmpty from 'lodash-es/isEmpty';

/* Ott-common */
import InteractiveElement from '@dtvgo/interactive-element';
import Skeleton from '@dtvgo/skeleton';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Components */
import LiveProgressBar from '../LiveProgressBar';
import InteractiveIcon from '../../assets/svg/interactive-icon.svg?react';

/* Utils */
import { Schedule } from '../../storage/models';
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import {
  useTitle, getSchedule, isInteractivityAllowed,
} from '../../utils/metaDataUtils';
import { useUserAccount } from '../../providers/UserProvider';
import { useIsSidebarExpanded } from '../../providers/SidebarStateContext';
import { noOp } from '../../utils/functions';
import useContent from '../../hooks/useContent';
import parentalControlBlock from '../../utils/parentalControlUtils';

/* Styles */
import './ScheduleCard.scss';

function ScheduleCard({
  className,
  schedule,
  onFocusedOrLiveContentChanged,
  isCurrent,
  focused,
  onMouseDown,
  onMouseEnter,
  isFetching: isFetchingProp,
  shouldStealFocus,
  setFocus,
  realFocusKey,
}) {
  const isFetching = !schedule && isFetchingProp;
  const {
    startTime,
    endTime,
    blackout,
    content: contentId,
  } = schedule || {};
  const liveContent = useContent(contentId);
  const isContentBlockedByParentalControl = parentalControlBlock(liveContent);
  const interactiveIcon = getJsonImage('interactive-icon');

  const title = useTitle(liveContent, schedule);
  const isSidebarExpanded = useIsSidebarExpanded();
  const { uid: userId, iso2Code } = useUserAccount() || {};
  const isInteractive = isInteractivityAllowed(schedule, iso2Code, userId);

  const isThereSchedules = useMemo(
    () => (startTime && endTime) && liveContent && !isEmpty(liveContent),
    [endTime, liveContent, startTime],
  );

  const { timeZone } = useIntl();

  const scheduleContent = useMemo(() => {
    if (isFetching) {
      return undefined;
    }

    return (schedule && isThereSchedules) && (
      <span className="dtv-html5-c-schedule-card__schedule">
        {getSchedule(schedule, timeZone)}
      </span>
    );
  }, [isFetching, isThereSchedules, schedule, timeZone]);

  const progressBar = useMemo(
    () => {
      if ((isCurrent && !blackout) && isFetching) {
        return undefined;
      }

      return (schedule && isCurrent && !blackout) && (
        <LiveProgressBar
          className="dtv-html5-c-schedule-card__progress-bar"
          startDate={startTime}
          endDate={endTime}
          focused={focused}
        />
      );
    },
    [blackout, endTime, focused, isCurrent, isFetching, schedule, startTime],
  );

  useEffect(() => {
    if (!isSidebarExpanded && shouldStealFocus) {
      setFocus();
    }
  }, [setFocus, shouldStealFocus, isSidebarExpanded]);

  useEffect(() => {
    if (isSidebarExpanded && shouldStealFocus) {
      sessionStorage.setItem('focusableItemInMain', realFocusKey);
    }
  }, [shouldStealFocus, isSidebarExpanded, realFocusKey]);

  useEffect(() => {
    if (focused) {
      onFocusedOrLiveContentChanged({ schedule, isCurrent });
    }
  }, [schedule, focused, isCurrent, onFocusedOrLiveContentChanged]);

  const metadata = useMemo(() => {
    if (title) {
      return (
        <div className="dtv-html5-c-schedule-card__title-container">
          <h4 className="dtv-html5-c-schedule-card__title">{title}</h4>
          {isInteractive && (
            <span className="dtv-html5-c-schedule-card__title-container-icon">
              {interactiveIcon ? (
                <img src={interactiveIcon} alt="interactive icon" />
              ) : (
                <InteractiveIcon />
              )}
            </span>
          )}
        </div>
      );
    }
    return (
      <span className="dtv-html5-c-schedule-card__missing-metadata">
        <FormattedMessage id="schedule.missing.metadata" />
      </span>
    );
  }, [title, isInteractive, interactiveIcon]);

  return (
    isFetching ? <Skeleton className="dtv-html5-c-schedule-card__skeleton" /> : (
      <InteractiveElement
        className={`dtv-html5-c-schedule-card ${focused ? 'dtv-html5-c-schedule-card--focused' : ''
        } ${isCurrent ? 'dtv-html5-c-schedule-card--current' : ''
        } ${blackout ? 'dtv-html5-c-schedule-card--inactive' : ''
        } ${className
        }`}
        onMouseDown={onMouseDown}
        onMouseEnter={onMouseEnter}
      >
        {!isFetching && metadata}
        {(isContentBlockedByParentalControl && !blackout) && (
          <div className="dtv-html5-c-schedule-card__parental-control">
            <span className="dtv-html5-c-schedule-card__parental-control-text">
              <FormattedMessage
                id="schedule.parentalControl.message"
                defaultMessage="Bloqueado por límite de edad"
              />
            </span>
          </div>
        )}
        <div className="dtv-html5-c-schedule-card__schedule-container">
          {blackout ? (
            <span className="dtv-html5-c-schedule-card__blackout-text">
              <FormattedMessage
                id="live.blackout"
                defaultMessage="Este contenido no se encuentra actualmente disponible en tu región"
              />
            </span>
          ) : scheduleContent}
          {isThereSchedules && progressBar}
        </div>
      </InteractiveElement>
    )
  );
}

ScheduleCard.propTypes = {
  className: PropTypes.string,
  schedule: PropTypes.instanceOf(Schedule),
  onFocusedOrLiveContentChanged: PropTypes.func,
  isCurrent: PropTypes.bool,
  focused: PropTypes.bool.isRequired,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
  isFetching: PropTypes.bool,
  shouldStealFocus: PropTypes.bool,
  setFocus: PropTypes.func.isRequired,
  realFocusKey: PropTypes.string.isRequired,
};

ScheduleCard.defaultProps = {
  className: '',
  schedule: undefined,
  onFocusedOrLiveContentChanged: noOp,
  isCurrent: false,
  onMouseDown: undefined,
  onMouseEnter: undefined,
  isFetching: false,
  shouldStealFocus: false,
};

export default memo(withInteractiveFocusable()(memo(ScheduleCard)));
